// CB:BEGIN post-award-panels
/**
 * PostAwardPanels — lightweight post-award workflow sections.
 * Covers: delay requests, completion verification, issue reporting,
 * permit confirmation, insurance alignment, warranty registration.
 * Role-aware: homeowner | contractor | admin.
 */
import React, { useState, useEffect } from 'react';
import { useApi } from '../lib/useApi';

const CARD: React.CSSProperties = {
  background: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
};

const SECTION_TITLE: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  marginBottom: 12,
  color: '#111827',
};

const BTN = (color: string = '#123c3b'): React.CSSProperties => ({
  background: color,
  color: 'white',
  border: 'none',
  borderRadius: 8,
  padding: '9px 18px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  marginRight: 8,
});

const BTN_OUTLINE: React.CSSProperties = {
  background: 'white',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  padding: '9px 18px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  marginRight: 8,
};

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 4,
};

const INPUT: React.CSSProperties = {
  width: '100%',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 14,
  marginBottom: 12,
  boxSizing: 'border-box',
};

const SELECT: React.CSSProperties = { ...INPUT, marginBottom: 12 };

const STATUS_BADGE = (color: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  background: color + '20',
  color: color,
  marginLeft: 8,
});

// ─── Delay Request Panel ─────────────────────────────────────────────────────

interface DelayPanelProps {
  projectId: string;
  role: 'homeowner' | 'contractor' | 'admin';
  projectStatus: string;
}

export const DelayRequestPanel: React.FC<DelayPanelProps> = ({ projectId, role, projectStatus }) => {
  const { get, post, patch } = useApi();
  const [delayRequests, setDelayRequests] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    delayReason: '',
    delayNotes: '',
    proposedNewStartDate: '',
  });

  const activeStatuses = ['IN_PROGRESS', 'ACTIVE', 'ACTIVE_P1', 'ACTIVE_P2', 'ACTIVE_P3', 'SCHEDULED'];

  useEffect(() => {
    if (!activeStatuses.includes(projectStatus)) return;
    get(`/api/projects/${projectId}/delay-requests`)
      .then((r: any) => setDelayRequests(r?.delayRequests || []))
      .catch(() => {});
  }, [projectId, projectStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!activeStatuses.includes(projectStatus)) return null;

  const pendingRequest = delayRequests.find((r: any) => r.status === 'PENDING');

  const submit = async () => {
    if (!form.delayReason || !form.proposedNewStartDate) return;
    setLoading(true);
    try {
      await post(`/api/projects/${projectId}/delay-request`, form);
      const r: any = await get(`/api/projects/${projectId}/delay-requests`);
      setDelayRequests(r?.delayRequests || []);
      setShowForm(false);
      setForm({ delayReason: '', delayNotes: '', proposedNewStartDate: '' });
    } catch (e) {
      alert('Failed to submit delay request');
    } finally {
      setLoading(false);
    }
  };

  const respond = async (requestId: string, action: 'accept' | 'reject') => {
    setLoading(true);
    try {
      await patch(`/api/projects/${projectId}/delay-request/${requestId}`, { action });
      const r: any = await get(`/api/projects/${projectId}/delay-requests`);
      setDelayRequests(r?.delayRequests || []);
    } catch (e) {
      alert('Failed to respond to delay request');
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (s: string) =>
    s === 'ACCEPTED' ? '#10b981' : s === 'REJECTED' ? '#ef4444' : '#f59e0b';

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={SECTION_TITLE}>Project Delay</h4>
        {role === 'contractor' && !pendingRequest && !showForm && (
          <button style={BTN_OUTLINE} onClick={() => setShowForm(true)}>Request Delay</button>
        )}
      </div>

      {/* Existing requests */}
      {delayRequests.map((req: any) => (
        <div key={req.id} style={{
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: 12,
          marginBottom: 8,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              {req.delayReason?.replace(/_/g, ' ')}
              <span style={STATUS_BADGE(statusColor(req.status))}>{req.status}</span>
            </span>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              New start: {new Date(req.proposedNewStartDate).toLocaleDateString()}
            </span>
          </div>
          {req.delayNotes && <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>{req.delayNotes}</p>}

          {/* Homeowner respond to pending */}
          {role === 'homeowner' && req.status === 'PENDING' && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <button style={BTN('#10b981')} disabled={loading} onClick={() => respond(req.id, 'accept')}>Accept</button>
              <button style={BTN('#ef4444')} disabled={loading} onClick={() => respond(req.id, 'reject')}>Reject</button>
            </div>
          )}
        </div>
      ))}

      {/* Contractor submit form */}
      {role === 'contractor' && showForm && (
        <div style={{ marginTop: 8 }}>
          <label style={LABEL}>Reason *</label>
          <select style={SELECT} value={form.delayReason} onChange={e => setForm({ ...form, delayReason: e.target.value })}>
            <option value="">Select reason...</option>
            {['WEATHER', 'SUPPLY_DELAY', 'PERMIT_DELAY', 'CREW_AVAILABILITY', 'OTHER'].map(r => (
              <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <label style={LABEL}>Notes (optional)</label>
          <textarea
            style={{ ...INPUT, resize: 'vertical', minHeight: 60 }}
            value={form.delayNotes}
            onChange={e => setForm({ ...form, delayNotes: e.target.value })}
            placeholder="Brief explanation..."
          />
          <label style={LABEL}>Proposed New Start Date *</label>
          <input type="date" style={INPUT} value={form.proposedNewStartDate} onChange={e => setForm({ ...form, proposedNewStartDate: e.target.value })} />
          <div style={{ marginTop: 4 }}>
            <button style={BTN()} disabled={loading} onClick={submit}>Submit Request</button>
            <button style={BTN_OUTLINE} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {delayRequests.length === 0 && !showForm && (
        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No delay requests</p>
      )}
    </div>
  );
};

// ─── Completion Verification Panel ───────────────────────────────────────────

interface CompletionPanelProps {
  projectId: string;
  role: 'homeowner' | 'contractor' | 'admin';
  projectStatus: string;
  onProjectUpdated?: () => void;
}

export const CompletionVerificationPanel: React.FC<CompletionPanelProps> = ({
  projectId, role, projectStatus, onProjectUpdated,
}) => {
  const { get, post, patch } = useApi();
  const [completion, setCompletion] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ completionNotes: '', cleanupConfirmed: false });

  const relevantStatuses = ['IN_PROGRESS', 'ACTIVE', 'ACTIVE_P1', 'ACTIVE_P2', 'ACTIVE_P3', 'COMPLETED'];

  useEffect(() => {
    if (!relevantStatuses.includes(projectStatus)) return;
    get(`/api/projects/${projectId}/completion`)
      .then((r: any) => setCompletion(r?.completion || null))
      .catch(() => {});
  }, [projectId, projectStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!relevantStatuses.includes(projectStatus)) return null;

  const submitCompletion = async () => {
    setLoading(true);
    try {
      const r: any = await post(`/api/projects/${projectId}/completion`, form);
      setCompletion(r?.completion);
      setShowForm(false);
    } catch {
      alert('Failed to submit completion');
    } finally {
      setLoading(false);
    }
  };

  const respondToCompletion = async (action: 'confirm' | 'report_issue') => {
    setLoading(true);
    try {
      const r: any = await patch(`/api/projects/${projectId}/completion/confirm`, { action });
      setCompletion(r?.completion);
      onProjectUpdated?.();
    } catch {
      alert('Failed to confirm completion');
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (s: string) =>
    s === 'HOMEOWNER_CONFIRMED' ? '#10b981' : s === 'ISSUE_REPORTED' ? '#ef4444' : s === 'CONTRACTOR_SUBMITTED' ? '#f59e0b' : '#6b7280';

  const statusLabel = (s: string) =>
    s === 'NOT_STARTED' ? 'Not submitted' :
    s === 'CONTRACTOR_SUBMITTED' ? 'Awaiting your confirmation' :
    s === 'HOMEOWNER_CONFIRMED' ? 'Confirmed complete' :
    s === 'ISSUE_REPORTED' ? 'Issue reported' : s;

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={SECTION_TITLE}>Project Completion</h4>
        {completion && (
          <span style={STATUS_BADGE(statusColor(completion.completionStatus))}>
            {statusLabel(completion.completionStatus)}
          </span>
        )}
      </div>

      {!completion && role === 'contractor' && projectStatus !== 'COMPLETED' && (
        <>
          {!showForm ? (
            <button style={BTN()} onClick={() => setShowForm(true)}>Mark as Complete</button>
          ) : (
            <>
              <label style={LABEL}>Completion Notes (optional)</label>
              <textarea
                style={{ ...INPUT, resize: 'vertical', minHeight: 72 }}
                value={form.completionNotes}
                onChange={e => setForm({ ...form, completionNotes: e.target.value })}
                placeholder="Describe work completed, any final notes..."
              />
              <label style={{ ...LABEL, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
                <input type="checkbox" checked={form.cleanupConfirmed} onChange={e => setForm({ ...form, cleanupConfirmed: e.target.checked })} />
                Site cleanup confirmed
              </label>
              <button style={BTN()} disabled={loading} onClick={submitCompletion}>Submit Completion</button>
              <button style={BTN_OUTLINE} onClick={() => setShowForm(false)}>Cancel</button>
            </>
          )}
        </>
      )}

      {completion?.completionStatus === 'CONTRACTOR_SUBMITTED' && role === 'homeowner' && (
        <div>
          {completion.completionNotes && (
            <p style={{ fontSize: 14, color: '#374151', marginBottom: 12 }}>{completion.completionNotes}</p>
          )}
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
            Cleanup confirmed: {completion.cleanupConfirmed ? '✅ Yes' : '⬜ Not confirmed'}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={BTN('#10b981')} disabled={loading} onClick={() => respondToCompletion('confirm')}>
              Confirm Completion
            </button>
            <button style={BTN('#ef4444')} disabled={loading} onClick={() => respondToCompletion('report_issue')}>
              Report Issue
            </button>
          </div>
        </div>
      )}

      {completion?.completionStatus === 'HOMEOWNER_CONFIRMED' && (
        <p style={{ fontSize: 14, color: '#10b981', fontWeight: 600 }}>
          ✅ Homeowner confirmed completion on {new Date(completion.confirmedAt).toLocaleDateString()}
        </p>
      )}

      {completion?.completionStatus === 'ISSUE_REPORTED' && (
        <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 600 }}>
          ⚠️ Issue reported — see Issues section below
        </p>
      )}

      {!completion && role !== 'contractor' && (
        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Awaiting contractor submission</p>
      )}
    </div>
  );
};

// ─── Issue Report Panel ───────────────────────────────────────────────────────

interface IssuePanelProps {
  projectId: string;
  role: 'homeowner' | 'contractor' | 'admin';
  projectStatus: string;
}

export const IssueReportPanel: React.FC<IssuePanelProps> = ({ projectId, role, projectStatus }) => {
  const { get, post, patch } = useApi();
  const [issues, setIssues] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [form, setForm] = useState({ issueCategory: '', issueDescription: '' });

  const relevantStatuses = ['IN_PROGRESS', 'ACTIVE', 'ACTIVE_P1', 'ACTIVE_P2', 'ACTIVE_P3', 'COMPLETED', 'CLOSED', 'DISPUTED'];

  useEffect(() => {
    if (!relevantStatuses.includes(projectStatus)) return;
    get(`/api/projects/${projectId}/issues`)
      .then((r: any) => setIssues(r?.issues || []))
      .catch(() => {});
  }, [projectId, projectStatus]);

  if (!relevantStatuses.includes(projectStatus) && issues.length === 0) return null;

  const submitIssue = async () => {
    if (!form.issueCategory || form.issueDescription.trim().length < 10) {
      alert('Please select a category and provide at least 10 characters of description');
      return;
    }
    setLoading(true);
    try {
      await post(`/api/projects/${projectId}/issues`, form);
      const r: any = await get(`/api/projects/${projectId}/issues`);
      setIssues(r?.issues || []);
      setShowForm(false);
      setForm({ issueCategory: '', issueDescription: '' });
    } catch {
      alert('Failed to report issue');
    } finally {
      setLoading(false);
    }
  };

  const submitResponse = async (issueId: string) => {
    if (!responseText.trim()) return;
    setLoading(true);
    try {
      await patch(`/api/projects/${projectId}/issues/${issueId}`, { contractorResponse: responseText });
      const r: any = await get(`/api/projects/${projectId}/issues`);
      setIssues(r?.issues || []);
      setRespondingTo(null);
      setResponseText('');
    } catch {
      alert('Failed to submit response');
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (s: string) =>
    s === 'RESOLVED' ? '#10b981' : s === 'REJECTED' ? '#6b7280' : s === 'UNDER_REVIEW' ? '#3b82f6' : '#ef4444';

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={SECTION_TITLE}>Issues {issues.length > 0 && `(${issues.length})`}</h4>
        {role === 'homeowner' && !showForm && (
          <button style={BTN_OUTLINE} onClick={() => setShowForm(true)}>Report Issue</button>
        )}
      </div>

      {issues.map((issue: any) => (
        <div key={issue.id} style={{
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: 12,
          marginBottom: 8,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              {issue.issueCategory?.replace(/_/g, ' ')}
              <span style={STATUS_BADGE(statusColor(issue.status))}>{issue.status?.replace(/_/g, ' ')}</span>
            </span>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>{new Date(issue.createdAt).toLocaleDateString()}</span>
          </div>
          <p style={{ fontSize: 13, color: '#374151', margin: '6px 0 0' }}>{issue.issueDescription}</p>
          {issue.contractorResponse && (
            <div style={{ marginTop: 8, background: '#eff6ff', borderRadius: 6, padding: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>Contractor response:</span>
              <p style={{ fontSize: 13, color: '#1e40af', margin: '2px 0 0' }}>{issue.contractorResponse}</p>
            </div>
          )}
          {role === 'contractor' && issue.status === 'OPEN' && respondingTo !== issue.id && (
            <button style={{ ...BTN_OUTLINE, marginTop: 8, fontSize: 13 }} onClick={() => setRespondingTo(issue.id)}>
              Respond
            </button>
          )}
          {respondingTo === issue.id && (
            <div style={{ marginTop: 8 }}>
              <textarea
                style={{ ...INPUT, resize: 'vertical', minHeight: 60 }}
                value={responseText}
                onChange={e => setResponseText(e.target.value)}
                placeholder="Brief response to the homeowner's concern..."
              />
              <button style={BTN()} disabled={loading} onClick={() => submitResponse(issue.id)}>Submit</button>
              <button style={BTN_OUTLINE} onClick={() => setRespondingTo(null)}>Cancel</button>
            </div>
          )}
        </div>
      ))}

      {role === 'homeowner' && showForm && (
        <div style={{ marginTop: 8 }}>
          <label style={LABEL}>Issue Category *</label>
          <select style={SELECT} value={form.issueCategory} onChange={e => setForm({ ...form, issueCategory: e.target.value })}>
            <option value="">Select category...</option>
            {['WORKMANSHIP', 'PROPERTY_DAMAGE', 'INCOMPLETE_WORK', 'CLEANUP', 'OTHER'].map(c => (
              <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <label style={LABEL}>Description *</label>
          <textarea
            style={{ ...INPUT, resize: 'vertical', minHeight: 80 }}
            value={form.issueDescription}
            onChange={e => setForm({ ...form, issueDescription: e.target.value })}
            placeholder="Describe the issue in detail (minimum 10 characters)..."
          />
          <button style={BTN('#ef4444')} disabled={loading} onClick={submitIssue}>Submit Issue</button>
          <button style={BTN_OUTLINE} onClick={() => setShowForm(false)}>Cancel</button>
        </div>
      )}

      {issues.length === 0 && !showForm && (
        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No issues reported</p>
      )}
    </div>
  );
};

// ─── Permit Panel ─────────────────────────────────────────────────────────────

interface PermitPanelProps {
  projectId: string;
  role: 'homeowner' | 'contractor' | 'admin';
  projectStatus: string;
}

export const PermitPanel: React.FC<PermitPanelProps> = ({ projectId, role, projectStatus }) => {
  const { get, put } = useApi();
  const [permit, setPermit] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    permitRequired: '',
    permitNumber: '',
    permitNotRequiredNote: '',
  });

  const relevantStatuses = ['SCHEDULED', 'IN_PROGRESS', 'ACTIVE', 'ACTIVE_P1', 'ACTIVE_P2', 'ACTIVE_P3', 'COMPLETED', 'CLOSED'];

  useEffect(() => {
    if (!relevantStatuses.includes(projectStatus)) return;
    get(`/api/projects/${projectId}/permit`)
      .then((r: any) => {
        setPermit(r?.permit || null);
        if (r?.permit) {
          setForm({
            permitRequired: r.permit.permitRequired ? 'true' : 'false',
            permitNumber: r.permit.permitNumber || '',
            permitNotRequiredNote: r.permit.permitNotRequiredNote || '',
          });
        }
      })
      .catch(() => {});
  }, [projectId, projectStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!relevantStatuses.includes(projectStatus)) return null;

  const save = async () => {
    if (form.permitRequired === '') { alert('Please indicate if a permit is required'); return; }
    setLoading(true);
    try {
      const payload = {
        permitRequired: form.permitRequired === 'true',
        permitNumber: form.permitRequired === 'true' ? form.permitNumber : undefined,
        permitNotRequiredNote: form.permitRequired === 'false' ? form.permitNotRequiredNote : undefined,
      };
      const r: any = await put(`/api/projects/${projectId}/permit`, payload);
      setPermit(r?.permit);
      setEditing(false);
    } catch {
      alert('Failed to save permit info');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={SECTION_TITLE}>Permit</h4>
        {role === 'contractor' && !editing && (
          <button style={BTN_OUTLINE} onClick={() => setEditing(true)}>{permit ? 'Update' : 'Add Permit Info'}</button>
        )}
      </div>

      {permit && !editing && (
        <div>
          <p style={{ fontSize: 14, margin: '0 0 4px', color: '#374151' }}>
            <strong>Required:</strong> {permit.permitRequired ? 'Yes' : 'No'}
          </p>
          {permit.permitRequired && permit.permitNumber && (
            <p style={{ fontSize: 14, margin: '0 0 4px', color: '#374151' }}>
              <strong>Permit #:</strong> {permit.permitNumber}
            </p>
          )}
          {!permit.permitRequired && permit.permitNotRequiredNote && (
            <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>{permit.permitNotRequiredNote}</p>
          )}
        </div>
      )}

      {!permit && !editing && (
        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
          {role === 'contractor' ? 'No permit info submitted yet' : 'Awaiting permit info from contractor'}
        </p>
      )}

      {editing && (
        <div>
          <label style={LABEL}>Permit required? *</label>
          <select style={SELECT} value={form.permitRequired} onChange={e => setForm({ ...form, permitRequired: e.target.value })}>
            <option value="">Select...</option>
            <option value="true">Yes — permit required</option>
            <option value="false">No — permit not required</option>
          </select>
          {form.permitRequired === 'true' && (
            <>
              <label style={LABEL}>Permit Number</label>
              <input type="text" style={INPUT} value={form.permitNumber} onChange={e => setForm({ ...form, permitNumber: e.target.value })} placeholder="e.g. 2026-BLD-04512" />
            </>
          )}
          {form.permitRequired === 'false' && (
            <>
              <label style={LABEL}>Reason (optional)</label>
              <input type="text" style={INPUT} value={form.permitNotRequiredNote} onChange={e => setForm({ ...form, permitNotRequiredNote: e.target.value })} placeholder="e.g. Residential re-roof under 1,000 sq ft" />
            </>
          )}
          <button style={BTN()} disabled={loading} onClick={save}>Save</button>
          <button style={BTN_OUTLINE} onClick={() => setEditing(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
};

// ─── Insurance Alignment Panel ────────────────────────────────────────────────

interface InsurancePanelProps {
  projectId: string;
  role: 'homeowner' | 'contractor' | 'admin';
  isInsuranceClaim?: boolean;
}

export const InsuranceAlignmentPanel: React.FC<InsurancePanelProps> = ({ projectId, role, isInsuranceClaim }) => {
  const { get, put } = useApi();
  const [alignment, setAlignment] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    scopeAligned: '',
    supplementRequested: '',
    alignmentNote: '',
  });

  useEffect(() => {
    if (!isInsuranceClaim) return;
    get(`/api/projects/${projectId}/insurance-alignment`)
      .then((r: any) => {
        const a = r?.insuranceAlignment;
        setAlignment(a);
        if (a) {
          setForm({
            scopeAligned: a.scopeAligned === true ? 'true' : a.scopeAligned === false ? 'false' : '',
            supplementRequested: a.supplementRequested === true ? 'true' : a.supplementRequested === false ? 'false' : '',
            alignmentNote: a.alignmentNote || '',
          });
        }
      })
      .catch(() => {});
  }, [projectId, isInsuranceClaim]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isInsuranceClaim) return null;

  const save = async () => {
    if (form.scopeAligned === '') { alert('Please indicate if scope is aligned'); return; }
    setLoading(true);
    try {
      const r: any = await put(`/api/projects/${projectId}/insurance-alignment`, {
        scopeAligned: form.scopeAligned === 'true',
        supplementRequested: form.supplementRequested ? form.supplementRequested === 'true' : undefined,
        alignmentNote: form.alignmentNote || undefined,
      });
      setAlignment(r?.insuranceClaim);
      setEditing(false);
    } catch {
      alert('Failed to save insurance alignment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={SECTION_TITLE}>Insurance Scope Alignment</h4>
        {role === 'contractor' && !editing && (
          <button style={BTN_OUTLINE} onClick={() => setEditing(true)}>
            {alignment?.alignmentSubmittedAt ? 'Update' : 'Submit Alignment'}
          </button>
        )}
      </div>

      {alignment?.alignmentSubmittedAt && !editing ? (
        <div>
          <p style={{ fontSize: 14, margin: '0 0 4px', color: '#374151' }}>
            <strong>Scope aligned with insurance:</strong>{' '}
            <span style={{ color: alignment.scopeAligned ? '#10b981' : '#ef4444' }}>
              {alignment.scopeAligned ? 'Yes' : 'No'}
            </span>
          </p>
          {alignment.supplementRequested !== null && alignment.supplementRequested !== undefined && (
            <p style={{ fontSize: 14, margin: '0 0 4px', color: '#374151' }}>
              <strong>Supplement requested:</strong>{' '}
              {alignment.supplementRequested ? 'Yes' : 'No'}
            </p>
          )}
          {alignment.alignmentNote && (
            <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>{alignment.alignmentNote}</p>
          )}
        </div>
      ) : !editing ? (
        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
          {role === 'contractor' ? 'No alignment info submitted yet' : 'Awaiting alignment confirmation from contractor'}
        </p>
      ) : null}

      {editing && (
        <div>
          <label style={LABEL}>Does your scope match the insurance scope? *</label>
          <select style={SELECT} value={form.scopeAligned} onChange={e => setForm({ ...form, scopeAligned: e.target.value })}>
            <option value="">Select...</option>
            <option value="true">Yes — scope matches</option>
            <option value="false">No — there are differences</option>
          </select>
          <label style={LABEL}>Supplement requested?</label>
          <select style={SELECT} value={form.supplementRequested} onChange={e => setForm({ ...form, supplementRequested: e.target.value })}>
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
          <label style={LABEL}>Notes (optional)</label>
          <textarea
            style={{ ...INPUT, resize: 'vertical', minHeight: 60 }}
            value={form.alignmentNote}
            onChange={e => setForm({ ...form, alignmentNote: e.target.value })}
            placeholder="Brief note on any scope differences or supplement details..."
          />
          <button style={BTN()} disabled={loading} onClick={save}>Save</button>
          <button style={BTN_OUTLINE} onClick={() => setEditing(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
};

// ─── Warranty Panel ───────────────────────────────────────────────────────────

interface WarrantyPanelProps {
  projectId: string;
  role: 'homeowner' | 'contractor' | 'admin';
  projectStatus: string;
}

export const WarrantyPanel: React.FC<WarrantyPanelProps> = ({ projectId, role, projectStatus }) => {
  const { get, put } = useApi();
  const [warranty, setWarranty] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    manufacturerWarrantyRegistered: false,
    workmanshipWarrantySummary: '',
    warrantyExpirationDate: '',
  });

  const completedStatuses = ['COMPLETED', 'CLOSED'];

  useEffect(() => {
    if (!completedStatuses.includes(projectStatus)) return;
    get(`/api/projects/${projectId}/warranty`)
      .then((r: any) => {
        const w = r?.warranty;
        setWarranty(w);
        if (w) {
          setForm({
            manufacturerWarrantyRegistered: !!w.manufacturerWarrantyRegistered,
            workmanshipWarrantySummary: w.workmanshipWarrantySummary || '',
            warrantyExpirationDate: w.warrantyExpirationDate ? w.warrantyExpirationDate.split('T')[0] : '',
          });
        }
      })
      .catch(() => {});
  }, [projectId, projectStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!completedStatuses.includes(projectStatus)) return null;

  const save = async () => {
    setLoading(true);
    try {
      const r: any = await put(`/api/projects/${projectId}/warranty`, {
        ...form,
        warrantyExpirationDate: form.warrantyExpirationDate || undefined,
      });
      setWarranty(r?.warranty);
      setEditing(false);
    } catch {
      alert('Failed to save warranty info');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={SECTION_TITLE}>Warranty Information</h4>
        {role === 'contractor' && !editing && (
          <button style={BTN_OUTLINE} onClick={() => setEditing(true)}>
            {warranty ? 'Update' : 'Add Warranty Info'}
          </button>
        )}
      </div>

      {warranty && !editing ? (
        <div>
          <p style={{ fontSize: 14, margin: '0 0 4px', color: '#374151' }}>
            <strong>Manufacturer warranty registered:</strong>{' '}
            {warranty.manufacturerWarrantyRegistered ? '✅ Yes' : '⬜ No'}
          </p>
          {warranty.workmanshipWarrantySummary && (
            <p style={{ fontSize: 14, margin: '0 0 4px', color: '#374151' }}>
              <strong>Workmanship:</strong> {warranty.workmanshipWarrantySummary}
            </p>
          )}
          {warranty.warrantyExpirationDate && (
            <p style={{ fontSize: 14, margin: '0 0 4px', color: '#374151' }}>
              <strong>Expires:</strong> {new Date(warranty.warrantyExpirationDate).toLocaleDateString()}
            </p>
          )}
        </div>
      ) : !editing ? (
        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
          {role === 'contractor' ? 'No warranty info submitted yet' : 'Awaiting warranty info from contractor'}
        </p>
      ) : null}

      {editing && (
        <div>
          <label style={{ ...LABEL, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={form.manufacturerWarrantyRegistered}
              onChange={e => setForm({ ...form, manufacturerWarrantyRegistered: e.target.checked })}
            />
            Manufacturer warranty registered
          </label>
          <label style={LABEL}>Workmanship warranty summary</label>
          <input
            type="text"
            style={INPUT}
            value={form.workmanshipWarrantySummary}
            onChange={e => setForm({ ...form, workmanshipWarrantySummary: e.target.value })}
            placeholder="e.g. 5-year workmanship warranty"
          />
          <label style={LABEL}>Warranty expiration date (optional)</label>
          <input
            type="date"
            style={INPUT}
            value={form.warrantyExpirationDate}
            onChange={e => setForm({ ...form, warrantyExpirationDate: e.target.value })}
          />
          <button style={BTN()} disabled={loading} onClick={save}>Save</button>
          <button style={BTN_OUTLINE} onClick={() => setEditing(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
};

// ─── Composite: all post-award panels for a role ──────────────────────────────

interface PostAwardSectionProps {
  projectId: string;
  role: 'homeowner' | 'contractor' | 'admin';
  projectStatus: string;
  isInsuranceClaim?: boolean;
  onProjectUpdated?: () => void;
}

export const PostAwardSection: React.FC<PostAwardSectionProps> = ({
  projectId, role, projectStatus, isInsuranceClaim = false, onProjectUpdated,
}) => {
  return (
    <>
      {/* CB:BEGIN post-award-delay */}
      <DelayRequestPanel projectId={projectId} role={role} projectStatus={projectStatus} />
      {/* CB:END post-award-delay */}

      {/* CB:BEGIN post-award-completion */}
      <CompletionVerificationPanel
        projectId={projectId}
        role={role}
        projectStatus={projectStatus}
        onProjectUpdated={onProjectUpdated}
      />
      {/* CB:END post-award-completion */}

      {/* CB:BEGIN post-award-issues */}
      <IssueReportPanel projectId={projectId} role={role} projectStatus={projectStatus} />
      {/* CB:END post-award-issues */}

      {/* CB:BEGIN post-award-permit */}
      <PermitPanel projectId={projectId} role={role} projectStatus={projectStatus} />
      {/* CB:END post-award-permit */}

      {/* CB:BEGIN post-award-insurance */}
      {isInsuranceClaim && (
        <InsuranceAlignmentPanel projectId={projectId} role={role} isInsuranceClaim={isInsuranceClaim} />
      )}
      {/* CB:END post-award-insurance */}

      {/* CB:BEGIN post-award-warranty */}
      <WarrantyPanel projectId={projectId} role={role} projectStatus={projectStatus} />
      {/* CB:END post-award-warranty */}
    </>
  );
};

export default PostAwardSection;
// CB:END post-award-panels
